"use client"

import * as React from 'react'
import Link from 'next/link'
import type { InjectionWidgetComponentProps } from '@open-mercato/shared/modules/widgets/injection'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { Badge } from '@open-mercato/ui/primitives/badge'

type ComplianceDocItem = {
  status: string
  document_type: string
  document_number: string
  valid_until: string | null
}

type ComplianceListResponse = {
  items?: Array<Record<string, unknown>>
  total?: number
}

type StatusCounts = {
  valid: number
  expiringSoon: number
  expired: number
  rejected: number
  total: number
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function readCompanyId(context: unknown, data: unknown): string | null {
  const ctx = context && typeof context === 'object' ? context as Record<string, unknown> : {}
  const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {}
  const company = payload.company && typeof payload.company === 'object'
    ? payload.company as Record<string, unknown>
    : {}
  return normalizeText(ctx.companyId)
    ?? normalizeText(ctx.resourceId)
    ?? normalizeText(company.id)
}

function countByStatus(items: ComplianceDocItem[]): StatusCounts {
  let valid = 0
  let expiringSoon = 0
  let expired = 0
  let rejected = 0

  for (const item of items) {
    switch (item.status) {
      case 'VALID': valid++; break
      case 'EXPIRING_SOON': expiringSoon++; break
      case 'EXPIRED': expired++; break
      case 'REJECTED': rejected++; break
      default: valid++; break
    }
  }

  return { valid, expiringSoon, expired, rejected, total: items.length }
}

function mapDocItem(raw: Record<string, unknown>): ComplianceDocItem {
  return {
    status: typeof raw.status === 'string' ? raw.status : 'VALID',
    document_type: typeof raw.document_type === 'string' ? raw.document_type : '',
    document_number: typeof raw.document_number === 'string' ? raw.document_number : '',
    valid_until: typeof raw.valid_until === 'string' ? raw.valid_until : null,
  }
}

function StatusBadge({ count, label, variant }: { count: number; label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }) {
  if (count === 0) return null
  return <Badge variant={variant}>{count} {label}</Badge>
}

export default function ComplianceStatusBadgeWidget({ context, data }: InjectionWidgetComponentProps) {
  const t = useT()
  const companyId = React.useMemo(() => readCompanyId(context, data), [context, data])
  const [participantId, setParticipantId] = React.useState<string | null>(null)
  const [counts, setCounts] = React.useState<StatusCounts | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false

    async function load() {
      if (!companyId) {
        if (!cancelled) { setLoading(false); setCounts(null); setParticipantId(null) }
        return
      }

      setLoading(true)
      setError(null)

      try {
        const participantResponse = await readApiResultOrThrow<{ items?: Array<Record<string, unknown>> }>(
          `/api/participants?customerEntityId=${encodeURIComponent(companyId)}&page=1&pageSize=1`,
          undefined,
          { errorMessage: t('compliance.widget.loadError', 'Failed to load compliance status.') },
        )
        if (cancelled) return

        const profile = Array.isArray(participantResponse.items) && participantResponse.items.length > 0
          ? participantResponse.items[0]
          : null

        if (!profile || typeof profile.id !== 'string') {
          setParticipantId(null)
          setCounts(null)
          setLoading(false)
          return
        }

        const profileId = profile.id as string
        setParticipantId(profileId)

        const docsResponse = await readApiResultOrThrow<ComplianceListResponse>(
          `/api/compliance/documents?participantProfileId=${encodeURIComponent(profileId)}&page=1&pageSize=200`,
          undefined,
          { errorMessage: t('compliance.widget.loadError', 'Failed to load compliance status.') },
        )
        if (cancelled) return

        const items = Array.isArray(docsResponse.items)
          ? docsResponse.items.map((raw) => mapDocItem(raw as Record<string, unknown>))
          : []

        setCounts(countByStatus(items))
      } catch (loadError) {
        if (cancelled) return
        const message = loadError instanceof Error && loadError.message
          ? loadError.message
          : t('compliance.widget.loadError', 'Failed to load compliance status.')
        setError(message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [companyId, t])

  if (!companyId) return null

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{t('compliance.widget.title', 'Compliance status')}</div>
          <div className="text-xs text-muted-foreground">
            {t('compliance.widget.description', 'Document compliance summary for this company.')}
          </div>
        </div>
        {participantId ? (
          <Link
            href={`/backend/compliance-documents?participantProfileId=${encodeURIComponent(participantId)}`}
            className="text-xs text-primary hover:underline"
          >
            {t('compliance.widget.actions.viewAll', 'View all')}
          </Link>
        ) : null}
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">{t('compliance.widget.loading', 'Loading compliance status…')}</div>
      ) : null}

      {!loading && error ? (
        <div className="text-xs text-destructive">{error}</div>
      ) : null}

      {!loading && !error && !participantId ? (
        <div className="text-xs text-muted-foreground">
          {t('compliance.widget.noProfile', 'No freight profile linked. Compliance tracking requires a participant profile.')}
        </div>
      ) : null}

      {!loading && !error && participantId && counts ? (
        counts.total === 0 ? (
          <div className="text-xs text-muted-foreground">
            {t('compliance.widget.noDocs', 'No compliance documents uploaded yet.')}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <StatusBadge count={counts.valid} label={t('compliance.widget.valid', 'valid')} variant="default" />
            <StatusBadge count={counts.expiringSoon} label={t('compliance.widget.expiring', 'expiring')} variant="secondary" />
            <StatusBadge count={counts.expired} label={t('compliance.widget.expired', 'expired')} variant="destructive" />
            <StatusBadge count={counts.rejected} label={t('compliance.widget.rejected', 'rejected')} variant="outline" />
          </div>
        )
      ) : null}
    </div>
  )
}
